import React from "react";
import {
  Pagination,
  PaginationGap,
  PaginationList,
  PaginationNext,
  PaginationPrevious,
  PaginationPage,
} from "@/components/pagination";
import { Button } from "@/components/button";

interface PaginationComponentProps {
  currentPage: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  onPageChange: (page: number) => void;
}

export const PaginationComponent: React.FC<PaginationComponentProps> = ({
  currentPage,
  totalPages,
  hasNextPage,
  hasPrevPage,
  onPageChange,
}) => {
  // Generate page numbers for pagination
  const getPageNumbers = () => {
    if (!totalPages) return [];

    const pageNumbers: number[] = [];

    // Maximum 7 page numbers to display
    if (totalPages <= 7) {
      // If total pages are 7 or fewer, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always include first page, last page, and current page
      // Plus one page before and after current page

      // First page
      pageNumbers.push(1);

      // Pages around current page
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);

      // Gap between first page and startPage if needed
      if (startPage > 2) {
        pageNumbers.push(-1); // Special value for gap
      }

      // Pages around current page
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      // Gap between endPage and last page if needed
      if (endPage < totalPages - 1) {
        pageNumbers.push(-2); // Special value for gap
      }

      // Last page
      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  if (totalPages <= 1) {
    return null;
  }

  return (
    <Pagination>
      <Button
        plain
        onClick={() => hasPrevPage && onPageChange(currentPage - 1)}
        disabled={!hasPrevPage}
        aria-label="Previous page"
      >
        <svg
          className="stroke-current"
          data-slot="icon"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M2.75 8H13.25M2.75 8L5.25 5.5M2.75 8L5.25 10.5"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Previous
      </Button>

      <PaginationList>
        {getPageNumbers().map((pageNum, index) => {
          if (pageNum < 0) {
            return <PaginationGap key={`gap-${index}`} />;
          }

          return (
            <Button
              key={pageNum}
              plain
              aria-label={`Page ${pageNum}`}
              aria-current={pageNum === currentPage ? "page" : undefined}
              onClick={() => onPageChange(pageNum)}
              className={`min-w-9 before:absolute before:-inset-px before:rounded-lg ${
                pageNum === currentPage
                  ? "before:bg-zinc-950/5 dark:before:bg-white/10"
                  : ""
              }`}
            >
              <span className="-mx-0.5">{pageNum}</span>
            </Button>
          );
        })}
      </PaginationList>

      <Button
        plain
        onClick={() => hasNextPage && onPageChange(currentPage + 1)}
        disabled={!hasNextPage}
        aria-label="Next page"
      >
        Next
        <svg
          className="stroke-current"
          data-slot="icon"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M13.25 8L2.75 8M13.25 8L10.75 10.5M13.25 8L10.75 5.5"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Button>
    </Pagination>
  );
};
